import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { money, date } from "@/lib/format";
import type { DashboardStats, Shipment } from "@/lib/types";
import { Button, Card, EmptyState, PageHeader, Skeleton, Stat, StatusBadge } from "@/components/ui";
import { useAuth } from "@/state/auth";

interface DashboardResponse {
  stats: DashboardStats;
  recent: Shipment[];
}

export function Dashboard() {
  const { account } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => api.get<DashboardResponse>("/v1/dashboard"),
    refetchInterval: 60_000,
  });

  const stats = data?.stats;
  const tiles = [
    { label: "Open shipments", value: stats?.active ?? 0 },
    { label: "In transit", value: stats?.in_transit ?? 0 },
    { label: "Delivered this month", value: stats?.delivered_this_month ?? 0 },
    { label: "Needs attention", value: stats?.exceptions ?? 0, alert: (stats?.exceptions ?? 0) > 0 },
  ];

  return (
    <>
      <PageHeader
        title={`Hello, ${account?.trading_name || account?.legal_name || "there"}`}
        subtitle="Everything moving right now, at a glance."
      />

      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {tiles.map((tile) => (
          <Stat
            key={tile.label}
            label={tile.label}
            value={tile.value}
            tone={tile.alert ? "bad" : "neutral"}
            loading={isLoading}
          />
        ))}
      </div>

      <Card className="mb-6">
        <p className="text-sm text-ink-400">Shipping spend this month</p>
        {isLoading ? (
          <Skeleton className="mt-2 h-9 w-40" />
        ) : (
          <p className="mt-1 text-3xl font-semibold tracking-tight">{money(stats?.spend_this_month_cents)}</p>
        )}
        <p className="mt-1 text-xs text-ink-400">Includes insurance where selected.</p>
      </Card>

      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-lg font-semibold tracking-tight">Recent shipments</h2>
        <Link to="/app/shipments" className="text-sm font-medium text-brand">
          View all
        </Link>
      </div>

      {isLoading ? (
        <Card>
          <Skeleton className="h-40 w-full" />
        </Card>
      ) : !data?.recent.length ? (
        <EmptyState
          title="No shipments yet"
          body="Book your first collection and it will appear here with live tracking."
          action={
            <Link to="/app/book">
              <Button>Book a shipment</Button>
            </Link>
          }
        />
      ) : (
        <div className="ws-card divide-y divide-ink-200">
          {data.recent.map((shipment) => (
            <Link
              key={shipment.id}
              to={`/app/shipments/${shipment.id}`}
              className="flex flex-wrap items-center gap-3 px-5 py-4 transition hover:bg-ink-100"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">
                  {shipment.delivery.city}
                  <span className="text-ink-400"> from </span>
                  {shipment.collection.city}
                </p>
                <p className="mt-0.5 font-mono text-xs text-ink-400">{shipment.waybill}</p>
              </div>
              <div className="text-right text-sm">
                <p className="font-medium">{money(shipment.price_cents)}</p>
                <p className="text-xs text-ink-400">{date(shipment.created_at)}</p>
              </div>
              <StatusBadge status={shipment.status} />
            </Link>
          ))}
        </div>
      )}
    </>
  );
}
